import { BE_BASE_URL } from './../config';
const API_BASE_URL = `${BE_BASE_URL}/api/`;

const UPLOAD_ENDPOINT = `${API_BASE_URL}input_file/`;
const ANALYSIS_CONFIG_ENDPOINT = `${API_BASE_URL}send_analysis_config/`;
const LOAD_CATEGORIES_ENDPOINT = `${API_BASE_URL}load_categories/`;
const GET_CONFIG_ENDPOINT = `${API_BASE_URL}get_config/`;
const START_ANALYSIS_ENDPOINT = `${API_BASE_URL}start_analysis/`;
const GET_ANALYSIS_SUMMARY_ENDPOINT = `${API_BASE_URL}analysis_summary/`;
const DOWNLOAD_RESULT_ENDPOINT = `${API_BASE_URL}download_result/`;
const GET_DASHBOARD_ENDPOINT = `${API_BASE_URL}dashboard_data/`;
const DELETE_ANALYSIS_ENDPOINT = `${API_BASE_URL}delete_analysis/`;

const LOGIN_ENDPOINT = `${API_BASE_URL}auth/login/`;
const LOGOUT_ENDPOINT = `${API_BASE_URL}auth/logout/`;
const CONFIRM_EMAIL_ENDPOINT = `${API_BASE_URL}auth/confirm-email/`;
const FORGOT_PASSWORD_ENDPOINT = `${API_BASE_URL}auth/request-password-reset/`;
const RESET_PASSWORD_ENDPOINT = `${API_BASE_URL}auth/password-reset/`;
const AUTHENTICATE_TMP_EMAIL_ENDPOINT = `${API_BASE_URL}auth/tmp-email/`;
const ACCOUNT_ENDPOINT = `${API_BASE_URL}auth/account/`;
const ACCOUNT_UPDATE_ENDPOINT = `${API_BASE_URL}auth/update/`;
const CHANGE_PASSWORD_ENDPOINT = `${API_BASE_URL}auth/change-password/`;
const DELETE_ACCOUNT_ENDPOINT = `${API_BASE_URL}auth/delete-account/`;

const CHECKOUT_ENDPOINT = `${API_BASE_URL}create-checkout-session/`;
const CHECK_AUTH_ENDPOINT = `${API_BASE_URL}check_auth/`;
const PAYMENT_HISTORY_ENDPOINT = `${API_BASE_URL}get-payment-history/`;
const DOWNLOAD_INVOICE_ENDPOINT = `${API_BASE_URL}download_invoice/`;
const GET_PROFORMA_ENDPOINT = `${API_BASE_URL}get_proforma/`;
const GET_CSRF_ENDPOINT = `${API_BASE_URL}get-csrf/`;

let csrfToken = null;

/**
 * Ensures we have a CSRF token. 
 * Call this before sensitive operations if you suspect the token is missing.
 */
export async function refreshCsrfToken() {
  try {
    const response = await fetch(GET_CSRF_ENDPOINT, {
      method: 'GET',
      credentials: 'include' // Important: ensures the cookie is also set/updated by browser
    });
    const data = await response.json();
    csrfToken = data.csrfToken; // 2. Store the token from JSON
    return csrfToken;
  } catch (error) {
    console.error("Failed to fetch CSRF token:", error);
    return null;
  }
}

/**
 * Generic API response handler
 * @param {Response} response - The fetch response object
 * @returns {Promise<any>} - The JSON response
 * @throws {Error} - Throws an error with the server's message if response is not ok
 */
async function handleApiResponse(response) {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Server error with no JSON response' }));
    throw new Error(errorData.message || `HTTP error! Status: ${response.status}`);
  }
  return response.json();
}

/**
 * Send user email for temporary analysis. Backend returns error or cookie if success
 */
export async function authenticateTmpEmail(email) {
    if (!csrfToken) await refreshCsrfToken();

    try {
        const response = await fetch(AUTHENTICATE_TMP_EMAIL_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "X-CSRFToken": csrfToken },
            body: JSON.stringify({email: email}),
            credentials: 'include'
        });

        const data = await handleApiResponse(response); 
        if (data.csrfToken) {
            csrfToken = data.csrfToken; 
        }
        return data;
    } catch (error) {
        console.error('Analysis Config Error:', error);
        throw new Error(error.message || 'Network error during analysis.');
    }
};


/**
 * Handles the file upload and validation call.
 * This function is specifically designed to send FormData.
 *
 * @param {File} file
 * @returns {Promise<{status: string, message: string}>}
 */
export async function uploadAndValidateFile(file) {
  const formData = new FormData();
  formData.append('questionary_file', file); 

  try {
    const response = await fetch(UPLOAD_ENDPOINT, {
      method: 'POST',
      headers: { "X-CSRFToken": csrfToken },
      body: formData,
      credentials: 'include'
    });

    // Auth error
    if (response.status === 401 || response.status === 403) {
      window.dispatchEvent(new CustomEvent('session-expired'));
      throw new Error('Authentication required'); 
    }

    return await handleApiResponse(response);
  } catch (error) {
    console.error('API Call Error:', error);
    throw new Error(error.message || 'Network error during file upload.');
  }
}

/**
 * Send analysis data and receive payment info + handle question type
 */
export async function sendAnalysisConfig(payload) {
    if (!csrfToken) await refreshCsrfToken();

    try {
        const response = await fetch(ANALYSIS_CONFIG_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "X-CSRFToken": csrfToken },
            body: JSON.stringify(payload),
            credentials: 'include'
        });

        const data = await handleApiResponse(response); 
        if (data.csrfToken) {
            csrfToken = data.csrfToken; 
        }
        return data;
    } catch (error) {
        console.error('Send analysis Config Error:', error);
        throw new Error(error.message || 'Network error during analysis.');
    }
};



/**
 * Handles request to suggest categories, used in 'llm' mode
 */
export async function loadCategories(payload) {
    if (!csrfToken) await refreshCsrfToken();

    try {
        const response = await fetch(LOAD_CATEGORIES_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "X-CSRFToken": csrfToken },
            body: JSON.stringify(payload),
            credentials: 'include'
        });

        const data = await handleApiResponse(response); 
        if (data.csrfToken) {
            csrfToken = data.csrfToken; 
        }
        return data;
    } catch (error) {
        console.error('Load categories Error:', error);
        throw new Error(error.message || 'Network error during analysis.');
    }
};

/**
 * Handles start of analysis.
 * @param {object} payload - The configuration data to send.
 */
export async function startAnalysis(payload) {
    try {
        const response = await fetch(START_ANALYSIS_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "X-CSRFToken": csrfToken },
            body: JSON.stringify(payload),
            credentials: 'include'
        });

        return await handleApiResponse(response);
    } catch (error) {
        console.error('Analysis Config Error:', error);
        throw new Error(error.message || 'Network error during analysis.');
    }
};

/**
 * Gets config summary after returning from payment or when returning to unfinished analysis
 */
export async function getConfigSummary(fileId) {
    if (!csrfToken) await refreshCsrfToken();

    try {
        const queryParams = new URLSearchParams({ fileId: fileId }).toString();
        const url = `${GET_CONFIG_ENDPOINT}?${queryParams}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', "X-CSRFToken": csrfToken },
            credentials: 'include'
        });

        return await handleApiResponse(response);
    } catch (error) {
        console.error('Analysis Config Error:', error);
        throw new Error(error.message || 'Network error during analysis.');
    }
};

/**
 * Fetches the summary (statistics) for a given analysis ID.
 * @param {number} fileId - The ID of the analysis file
 */
export async function getAnalysisSummary(fileId) {
    try {
        const queryParams = new URLSearchParams({ fileId: fileId }).toString();
        const url = `${GET_ANALYSIS_SUMMARY_ENDPOINT}?${queryParams}`;

        const response = await fetch(url, {
            method: 'GET', 
            headers: { "X-CSRFToken": csrfToken },
            credentials: 'include'
        });
        return await handleApiResponse(response);
    } catch (error) {
        console.error('Get Summary Error:', error);
        throw new Error(error.message || 'Network error during summary fetch.');
    }
};

/**
 * Handles request to download the result file
 * @param {string} fileId - The ID of the file to download
 */
export async function downloadResult(fileId) {
    try {
      const response = await fetch(DOWNLOAD_RESULT_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify({ fileId }),
        credentials: "include",
      });

      if (!response.ok) {
        const text = await response.text(); // backend error message (HTML/text)
        throw new Error(text || `HTTP error ${response.status}`);
      }

      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "vysledek_analyzy.xlsx"; // same name as backend
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error("Download Result Error:", error);
      throw error;
    }
};


/**
 * Reusable helper function for auth API calls.
 * @param {string} endpoint - The API endpoint to call.
 * @param {object} payload - The data to send in the request body.
 * @param {string} [method='POST'] - The HTTP method.
 * @returns {Promise<object>} - The JSON response from the server.
 */
async function callAuthApi(endpoint, payload, method = 'POST') {
  if (!csrfToken) await refreshCsrfToken();

  try {
    const response = await fetch(endpoint, {
      method: method,
      headers: { 'Content-Type': 'application/json', "X-CSRFToken": csrfToken },
      body: JSON.stringify(payload),
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: 'Selhala komunikace se serverem. Prosím, opakujte akci později.',
      }));
      const message = errorData.detail || errorData.message || `HTTP error! Status: ${response.status}`;
      throw new Error(message);
    }

    if (response.csrfToken) {
        csrfToken = response.csrfToken; 
    }

    if (endpoint === LOGOUT_ENDPOINT) {
        await refreshCsrfToken();
    }
    
    if (response.status === 204) { // no content
        return { success: true };
    }

    return await response.json();

  } catch (error) {
    console.error('Auth API Error:', error);
    throw new Error(error.message || 'Network error during authentication.');
  }
}

/**
 * Logs in a user.
 * @param {object} credentials - { email, password }
 */
export async function loginUser(credentials) {
  return callAuthApi(LOGIN_ENDPOINT, credentials);
}

/**
 * Handles user logout
 */
export async function logoutUser() {
  return callAuthApi(LOGOUT_ENDPOINT);
}



/**
 * Confirms email address entered in registration.
 * @param {object} confirmEmailData - { email }
 */
export async function confirmEmail(confirmEmailData) {
  return callAuthApi(CONFIRM_EMAIL_ENDPOINT, confirmEmailData);
}

/**
 * Sends a password reset request.
 * @param {object} emailData - { email }
 */
export const requestPasswordReset = async (emailData) => {
    const response = await fetch(FORGOT_PASSWORD_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', "X-CSRFToken": csrfToken  },
        body: JSON.stringify(emailData),
        credentials: 'include',
    });

    const result = await response.json();

    if (!response.ok) {
        throw result; 
    }

    return result;
};

/**
 * Submits a new password with a reset token.
 * @param {object} payload - { uid, token, new_password }
 */
export async function resetPassword(payload) {
  return callAuthApi(RESET_PASSWORD_ENDPOINT, payload);
}

/**
 * Získá data o účtu aktuálně přihlášeného uživatele.
 */
export const getAccountData = async () => {
    const response = await fetch(ACCOUNT_ENDPOINT, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', "X-CSRFToken": csrfToken },
        credentials: 'include',
    });

    const result = await response.json();
    if (!response.ok) throw result;
    return result;
};

/**
 * Upraví data o účtu.
 * @param {object} accountData - data formuláře
 */
export const updateAccountData = async (accountData) => {
    const response = await fetch(ACCOUNT_UPDATE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', "X-CSRFToken": csrfToken },
        body: JSON.stringify(accountData),
        credentials: 'include',
    });

    const result = await response.json();
    if (!response.ok) throw result;
    return result;
};

/**
 * Změní heslo přihlášeného uživatele.
 * @param {object} passwordData - { currentPassword, newPassword }
 */
export const changePassword = async (passwordData) => {
    const response = await fetch(CHANGE_PASSWORD_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', "X-CSRFToken": csrfToken },
        body: JSON.stringify(passwordData),
        credentials: 'include',
    });

    const result = await response.json();
    if (!response.ok) throw result;
    return result;
};

export async function deleteAccount(password) {
    const response = await fetch(DELETE_ACCOUNT_ENDPOINT, {
        method: 'DELETE',
        headers: { 
            'Content-Type': 'application/json', 
            "X-CSRFToken": csrfToken
        },
        body: JSON.stringify({ password }),
        credentials: 'include',
    });
    const result = await response.json();

    if (!response.ok) {
        throw result; 
    }
    return result;
}

/**
 * Fetches dashboard data (credits, analysis list).
 */
export async function getDashboardData() {
    try {
        const response = await fetch(GET_DASHBOARD_ENDPOINT, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', "X-CSRFToken": csrfToken },
            credentials: 'include'
        });
        return await handleApiResponse(response);
    } catch (error) {
        console.error('Get Dashboard Error:', error);
        throw new Error(error.message || 'Network error during dashboard data fetch.');
    }
};

/**
 * Deletes analysis by id
 */
export async function deleteAnalysis(fileId) {
    const response = await fetch(DELETE_ANALYSIS_ENDPOINT, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', "X-CSRFToken": csrfToken  },
        body: JSON.stringify({fileId: fileId}),
        credentials: 'include',
    });

    const result = await response.json();

    if (!response.ok) {
        throw result; 
    }

    return result;
};

/**
 * Get the stripe payment url and redirect there
 */
export async function handleCheckout(credits_amount, fileId) {
  window.dataLayer.push({
    event: 'payment_redirect',
    value: credits_amount,
    currency: 'CZK'
  });
  try {
    const response = await fetch(CHECKOUT_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
      body: JSON.stringify({ credits: credits_amount, fileId: fileId }),
      credentials: 'include',
    });

    const data = await response.json();

    if (!response.ok) {
      if (data.message) {
        alert(data.message);
      } else {
        throw new Error("Failed to create checkout session");
      }
    }

    // Redirect the user to Stripe Checkout
    window.location.href = data.url;
  } catch (error) {
    console.error("Checkout error:", error);
    alert("Something went wrong creating the payment session.");
  }
}

/**
 * Checks with the backend if the user has a valid session.
 * Used on app load to maintain login state across reloads.
 */
export async function checkAuthStatus() {
  await refreshCsrfToken();

  try {
    const response = await fetch(CHECK_AUTH_ENDPOINT, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', "X-CSRFToken": csrfToken },
      credentials: 'include'
    });

    if (!response.ok) { // This is expected if not logged in (e.g., 401 Unauthorized)
      return { isAuthenticated: false };
    }

    const data = await response.json();
    // Returns { isAuthenticated: true, user: { ... } } or { isAuthenticated: false }
    return data;
  } catch (error) {
    console.error('Auth Status Check Error:', error);
    // On network error, assume not authenticated
    return { isAuthenticated: false };
  }
}

export async function getPaymentHistory() {
  try {
    const response = await fetch(PAYMENT_HISTORY_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', "X-CSRFToken": csrfToken },
      credentials: 'include'
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Server error' }));
      throw new Error(errorData.message || `HTTP error! Status: ${response.status}`);
    }
    return await response.json(); // Returns { payments: [...] }
  } catch (error) {
    console.error('Get Payment History Error:', error);
    throw new Error(error.message || 'Network error fetching payment history.');
  }
}

/**
 * Handles request to download invoice with specified payment id
 * @param {string} paymentId - The ID of the payment for which to download the invoice
 */
export async function downloadInvoice(paymentId) {
  try {
    const queryParams = new URLSearchParams({ paymentId: paymentId }).toString();
    const urlQuery = `${DOWNLOAD_INVOICE_ENDPOINT}?${queryParams}`;

    const response = await fetch(urlQuery, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken,
      },
      credentials: "include",
    });

    if (!response.ok) {
      const text = await response.text(); // backend error message (HTML/text)
      throw new Error(text || `HTTP error ${response.status}`);
    }

    const blob = await response.blob();

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "danovy_doklad_recodeq.pdf"; // same name as backend
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

  } catch (error) {
    console.error("Download Result Error:", error);
    throw error;
  }
};

/**
 * Handles request to get proforma invoice
 * @param amount - Amount for the invoice
 */
export async function getProforma(amount) {
  try {
    const queryParams = new URLSearchParams({ amount: amount }).toString();
    const urlQuery = `${GET_PROFORMA_ENDPOINT}?${queryParams}`;

    const response = await fetch(urlQuery, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken,
      },
      credentials: "include",
    });

    if (!response.ok) {
      const text = await response.text(); // backend error message (HTML/text)
      throw new Error(text || `HTTP error ${response.status}`);
    }

    const blob = await response.blob();

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "zalohova_faktura_recodeq.pdf"; // same name as backend
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

  } catch (error) {
    console.error("Download Result Error:", error);
    throw error;
  }
};

export async function updateImprovingAgree (improvingAgree) {
  if (!csrfToken) await refreshCsrfToken();

  try {
      const response = await fetch(`${API_BASE_URL}account/update-improving-agree/`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', "X-CSRFToken": csrfToken },
          body: JSON.stringify({
            improvingAgree: improvingAgree,
          }),
          credentials: 'include'
      });

      const data = await handleApiResponse(response); 
      if (data.csrfToken) {
          csrfToken = data.csrfToken; 
      }
      return data;
  } catch (error) {
      console.error('Update Improving Agree Error:', error);
      throw new Error(error.message || 'Network error during analysis.');
  }
};

export async function saveCategoryChanges(payload) {
  const response = await fetch(`${API_BASE_URL}save-category-changes/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', "X-CSRFToken": csrfToken },
    credentials: 'include',
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || 'Nepodařilo se uložit změny.');
  }

  return data;
}

export async function loadCategoryAnswers(payload) {
  const response = await fetch(`${API_BASE_URL}load-category-answers/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', "X-CSRFToken": csrfToken },
    credentials: 'include',
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || 'Nepodařilo se načíst odpovědi kategorie.');
  }

  return data;
}

export async function backendRequest({endpoint, method, data = null}) {
    const options = {
        method: method,
        headers: { "X-CSRFToken": csrfToken },
        credentials: "include",
        
    };

    // Only add JSON body when data exists
    if (data !== null && data !== undefined) {
        options.headers["Content-Type"] = "application/json";
        options.body = JSON.stringify(data);
    }

    const response = await fetch(`${BE_BASE_URL}${endpoint}`, options);

    const contentType = response.headers.get("content-type");
    const isJson = contentType && contentType.includes("application/json");

    const responseData = isJson ? await response.json() : await response.text();

    if (!response.ok) {
        const message =
            typeof responseData === "object" && responseData !== null
                ? responseData.message || responseData.detail || "Chyba komunikace se serverem."
                : responseData || "Chyba komunikace se serverem.";

        throw new Error(message);
    }

    return responseData;
}

/**
 * Registers a new user.
 * @param {object} userData - { email, password }
 */
export const registerUser = async (userData) => {
  return backendRequest({
      method: "POST",
      endpoint: `/api/auth/register/`,
      data: userData
  });
};

export const sendResultToEmail = async (fileId) => {
  return backendRequest({
      method: "POST",
      endpoint: `/api/analysis/${fileId}/send-result-email/`,
  });
};

export async function showResultPage(analysisId, pageNumber, query = '', categories = []) {
    return await backendRequest({
        endpoint: `/api/analysis/${analysisId}/detail/`,
        method: 'POST',
        data: {
            page: pageNumber,
            query: query,
            categories: categories
        }
    });
}


